import { useState, useEffect } from 'react';
import { Loader2, RotateCcw, ArrowRight, Clock, GripVertical } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const HomepageRotationTab = ({ headers }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/homepage/admin/settings`, { headers, credentials: 'include' });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error('Failed to fetch homepage settings:', e); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only


  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => { fetchSettings(); }, []);

  const updateSettings = async (updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/homepage/admin/settings`, {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify(updates),
      });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error('Failed to update homepage settings:', e); }
    finally { setSaving(false); }
  };

  const manualRotate = async () => {
    setRotating(true);
    try {
      const res = await fetch(`${API}/api/homepage/admin/rotate`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, current_page: data.current_page }));
      }
    } catch (e) { console.error('Failed to rotate homepage:', e); }
    finally { setRotating(false); }
  };

  const setCurrentPage = (pageId) => {
    updateSettings({ current_page: pageId });
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>;
  if (!settings) return <p className="text-sm text-slate-400 text-center py-12">Unable to load rotation settings.</p>;

  const allPages = settings.all_pages || [];
  const lastRotatedDate = settings.last_rotated ? new Date(settings.last_rotated) : null;
  const nextRotation = lastRotatedDate ? new Date(lastRotatedDate.getTime() + settings.cycle_days * 24 * 60 * 60 * 1000) : null;

  return (
    <div className="space-y-6" data-testid="admin-homepage-rotation">
      {/* Current State */}
      <div className="bg-gradient-to-r from-taxera-blue/5 to-taxera-teal/5 rounded-2xl border border-taxera-blue/10 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-taxera-charcoal mb-1">Current Homepage</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-taxera-blue capitalize">{settings.current_page}</span>
              <span className="text-2xs px-2 py-0.5 bg-taxera-blue/10 text-taxera-blue rounded-full font-semibold">Active</span>
            </div>
            {lastRotatedDate && (
              <p className="text-2xs text-slate-400 mt-1">Last rotated: {lastRotatedDate.toLocaleDateString()}</p>
            )}
            {nextRotation && settings.auto_rotate && (
              <p className="text-2xs text-slate-400">Next rotation: {nextRotation.toLocaleDateString()}</p>
            )}
          </div>
          <button onClick={manualRotate} disabled={rotating}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-taxera-blue text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            data-testid="manual-rotate-btn">
            {rotating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Rotate Now
          </button>
        </div>
      </div>

      {/* Auto-rotation Settings */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => updateSettings({ auto_rotate: !settings.auto_rotate })}
              className={`w-10 h-6 rounded-full transition-colors relative ${settings.auto_rotate ? 'bg-taxera-teal' : 'bg-slate-300'}`}
              data-testid="auto-rotate-toggle">
              <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${settings.auto_rotate ? 'left-5' : 'left-1'}`} />
            </button>
            <div>
              <span className="text-sm font-semibold text-slate-700">Auto-rotate homepage</span>
              <p className="text-2xs text-slate-400">Homepage cycles through pages automatically</p>
            </div>
          </div>
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">Cycle every</span>
          <select value={settings.cycle_days}
            onChange={(e) => updateSettings({ cycle_days: parseInt(e.target.value) })}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-taxera-teal"
            data-testid="cycle-days-select">
            {[1, 3, 5, 7, 10, 14, 21, 30].map(d => (
              <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Page Selector */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-taxera-charcoal mb-1">Set Homepage Directly</h3>
        <p className="text-2xs text-slate-400 mb-4">Click a page to make it the current homepage immediately.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {allPages.map(page => {
            const isCurrent = settings.current_page === page.id;
            const isInRotation = settings.rotation_order?.includes(page.id);
            return (
              <button key={page.id} onClick={() => !isCurrent && setCurrentPage(page.id)}
                className={`p-3 rounded-xl border text-left transition-all ${
                  isCurrent
                    ? 'border-taxera-blue bg-taxera-blue/5 ring-2 ring-taxera-blue/20'
                    : 'border-slate-200 hover:border-taxera-teal hover:bg-slate-50'
                }`}
                data-testid={`page-select-${page.id}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold ${isCurrent ? 'text-taxera-blue' : 'text-taxera-charcoal'}`}>
                    {page.label}
                  </span>
                  {isCurrent && <span className="text-2xs px-1.5 py-0.5 bg-taxera-blue text-white rounded-full font-semibold">Current</span>}
                </div>
                <span className="text-2xs text-slate-400 mt-0.5 block">{page.path}</span>
                {!isInRotation && (
                  <span className="text-2xs text-amber-500 mt-1 block">Not in rotation order</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Rotation Order */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-taxera-charcoal mb-1">Rotation Order</h3>
        <p className="text-2xs text-slate-400 mb-4">The sequence in which pages will rotate.</p>
        <div className="space-y-1.5">
          {(settings.rotation_order || []).map((pageId, idx) => {
            const page = allPages.find(p => p.id === pageId);
            if (!page) return null;
            const isCurrent = settings.current_page === pageId;
            return (
              <div key={pageId} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${
                isCurrent ? 'border-taxera-blue/30 bg-taxera-blue/5' : 'border-slate-100 bg-slate-50'
              }`} data-testid={`rotation-order-${idx}`}>
                <span className="text-2xs font-bold text-slate-400 w-5">{idx + 1}</span>
                <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                <span className={`text-sm font-medium ${isCurrent ? 'text-taxera-blue' : 'text-slate-600'}`}>{page.label}</span>
                {isCurrent && <ArrowRight className="w-3 h-3 text-taxera-blue ml-auto" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HomepageRotationTab;
